import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Exercies.css';
import Logo from './assets/Logo.png';
import { db } from '../Firebase';
import { doc, getDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const importAll = (r) => {
  let images = {};
  r.keys().map((item, index) => { 
    images[item.replace('./Level-', '').replace('.gif', '')] = r(item); 
    return item;
  });
  return images;
}

const levelImages = importAll(require.context('./assets', false, /Level-\d+\.gif$/));

function Exercies({ userData, onLogout }) { 
  const navigate = useNavigate();
  const currentLevel = userData?.level !== undefined ? userData.level : 0;
  const totalScore = userData?.score !== undefined ? `${userData.score} Points` : '... Points';
  const imageToDisplay = levelImages[currentLevel] || levelImages['0'];
  const isMaxLevelReached = currentLevel > 119;
  const [isCheckingBonus, setIsCheckingBonus] = useState(false);
  const [bonusStatus, setBonusStatus] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const checkBonusStatus = useCallback(async () => {
    if (!userData?.username) return false;
    
    setIsCheckingBonus(true);
    try {
      const userRef = doc(db, "assumption", userData.username);
      const docSnap = await getDoc(userRef);
      if (docSnap.exists()) {
        const bonus = docSnap.data().bonus;
        setBonusStatus(bonus);

        if (bonus && currentLevel < 119) {
          Swal.fire({
            title: "โบนัส!",
            text: "คุณจะได้รับโบนัสเมื่อเริ่มทำแบบฝึกหัดต่อไป",
            icon: "success",
            confirmButtonText: "รับทราบ"
          });
        }
      } else {
        console.warn("User data not found for bonus check.");
        setBonusStatus(false);
      }
    } catch (error) {
      console.error("Error checking bonus status:", error);
      setBonusStatus(false);
      Swal.fire({
        title: "ข้อผิดพลาด",
        text: "ไม่สามารถตรวจสอบสถานะโบนัสได้",
        icon: "error",
        confirmButtonText: "ตกลง"
      });
    } finally {
      setIsCheckingBonus(false);
    }
  }, [userData, currentLevel]);

  useEffect(() => {
    checkBonusStatus();
  }, [checkBonusStatus]);

  const handleStartExercise = () => {
    if (isMaxLevelReached) {
      Swal.fire({
        title: "เสร็จสิ้น!",
        text: "คุณได้ทำแบบฝึกหัดทั้งหมดแล้ว",
        icon: "info",
        confirmButtonText: "ตกลง"
      });
      return;
    }
    if (bonusStatus) {
        navigate('/match-game');
    } else {
        navigate('/video');
    }
  };

  const handleGoToLeaderboard = () => {
    navigate('/leaderboard');
  };

  // ฟังก์ชันสำหรับจัดการการซูม
  const handleZoom = (direction) => {
    if (direction === 'in') {
      setZoomLevel(prev => Math.min(prev + 0.2, 3));
    } else if (direction === 'out') {
      setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
    } else {
      setZoomLevel(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  // ฟังก์ชันสำหรับจัดการการลากภาพ
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // จำกัดขอบเขตการลาก
    const maxMove = 200;
    setImagePosition({
      x: Math.max(Math.min(newX, maxMove), -maxMove),
      y: Math.max(Math.min(newY, maxMove), -maxMove)
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // สำหรับอุปกรณ์ touch
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - imagePosition.x,
        y: e.touches[0].clientY - imagePosition.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    
    const newX = e.touches[0].clientX - dragStart.x;
    const newY = e.touches[0].clientY - dragStart.y;
    
    // จำกัดขอบเขตการลาก
    const maxMove = 200;
    setImagePosition({
      x: Math.max(Math.min(newX, maxMove), -maxMove),
      y: Math.max(Math.min(newY, maxMove), -maxMove)
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="exercies-container">
      <div 
        className="image-section"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {imageToDisplay ? (
          <img 
            src={imageToDisplay} 
            alt={`Level ${currentLevel}`} 
            className="full-image"
            style={{
              transform: `scale(${zoomLevel}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
              cursor: isDragging ? 'grabbing' : zoomLevel > 1 ? 'grab' : 'default',
              transition: isDragging ? 'none' : 'transform 0.2s ease'
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          />
        ) : (
          <div className="no-image">No image for this level.</div>
        )}
        
        {/* ปุ่มควบคุมการซูม */}
        <div className="zoom-controls">
          <button onClick={() => handleZoom('in')} aria-label="Zoom In">
            +
          </button>
          <button onClick={() => handleZoom('out')} aria-label="Zoom Out">
            -
          </button>
          <button onClick={() => handleZoom('reset')} aria-label="Reset Zoom">
            ↺
          </button>
        </div>
      </div>
      
      <div className="input-section">
        <div className="logo-container">
          <img src={Logo} alt="Logo" className="logo-image2"/>
        </div>
        <h2 className="section-title">แบบฝึกพัฒนาสมอง</h2>
        <h2 className="section-title">สร้างสมาธิ</h2>

        <div className="input-group level-display-group">
          <div className="display-level">Step {currentLevel}</div>
        </div>

        <div className="input-group score-display-group">
          <label className="input-label">Total Score</label>
          <div className="display-score">{totalScore}</div>
        </div>

        <button
          onClick={handleStartExercise}
          className={`solid-button ${isMaxLevelReached ? 'disabled-button' : ''}`}
          disabled={isMaxLevelReached || isCheckingBonus}
        >
          {isCheckingBonus ? 'กำลังตรวจสอบ...' : 
           isMaxLevelReached ? 'เสร็จสิ้น!' : 'เริ่มเลย!'}
        </button>
        
        {isMaxLevelReached && (
          <div className="completion-message">
            คุณได้ทำแบบฝึกหัดทั้งหมดแล้ว!
          </div>
        )}

        <button
          onClick={handleGoToLeaderboard}
          className="solid-button leaderboard-button"
          style={{ marginTop: '15px' }}
        >
          Leaderboard
        </button>

        {onLogout && (
          <button
            onClick={onLogout}
            className="solid-button"
            style={{ marginTop: '15px', backgroundColor: '#dc3545' }}
          >
            ออกจากระบบ
          </button>
        )}
        <p></p>
        <p>Logged in as: {userData.username}</p>
      </div>
    </div>
  );
}

export default Exercies;